import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getRestaurants,
  getRestaurant,
  updateRestaurant,
  suspendRestaurant,
  activateRestaurant,
  deleteRestaurant,
  changeRestaurantPlan,
} from '@/lib/api/admin/restaurants'
import type {
  RestaurantStatus,
  SubscriptionPlan,
  UpdateRestaurantDTO,
  ChangePlanRequestDTO,
} from '@/types/admin'

/**
 * Hook to fetch paginated restaurant list
 */
export function useRestaurants(params: {
  page?: number
  size?: number
  status?: RestaurantStatus
  plan?: SubscriptionPlan
  search?: string
}) {
  return useQuery({
    queryKey: ['admin', 'restaurants', params],
    queryFn: () => getRestaurants(params),
  })
}

/**
 * Hook to fetch single restaurant details
 */
export function useRestaurant(id: number) {
  return useQuery({
    queryKey: ['admin', 'restaurants', id],
    queryFn: () => getRestaurant(id),
    enabled: !!id,
  })
}

/**
 * Hook to update restaurant
 */
export function useUpdateRestaurant() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateRestaurantDTO }) =>
      updateRestaurant(id, data),
    onSuccess: (data, variables) => {
      // Invalidate restaurant list
      queryClient.invalidateQueries({ queryKey: ['admin', 'restaurants'] })
      // Update single restaurant cache
      queryClient.setQueryData(['admin', 'restaurants', variables.id], data)
      // Invalidate dashboard (metrics might change)
      queryClient.invalidateQueries({ queryKey: ['admin', 'dashboard'] })
    },
  })
}

/**
 * Hook to suspend restaurant
 */
export function useSuspendRestaurant() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => suspendRestaurant(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'restaurants'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'dashboard'] })
    },
  })
}

/**
 * Hook to activate restaurant
 */
export function useActivateRestaurant() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => activateRestaurant(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'restaurants'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'dashboard'] })
    },
  })
}

/**
 * Hook to delete restaurant
 */
export function useDeleteRestaurant() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => deleteRestaurant(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'restaurants'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'dashboard'] })
    },
  })
}

/**
 * Hook to change restaurant plan
 */
export function useChangeRestaurantPlan() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ restaurantId, data }: { restaurantId: number; data: ChangePlanRequestDTO }) =>
      changeRestaurantPlan(restaurantId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'restaurants'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'dashboard'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'subscriptions'] })
    },
  })
}
